import React, { useEffect, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { clearBackgroundLocalStorage } from '../../contexts/BackgroundContext';
import styles from './Sidebar.module.css';
import { BACKEND } from '../../config';
import { csrfFetch } from '../../utils/csrfFetch';

const Sidebar: React.FC = () => {
  const { role, refreshSession, username, isLawyer, isMediator } = useAuth();
  const [appVersion, setAppVersion] = useState('');
  const [updateStatus, setUpdateStatus] = useState<UpdateStatusPayload | null>(null);
  const [updateDiagnostics, setUpdateDiagnostics] = useState<UpdateDiagnostics | null>(null);
  const [isCheckingUpdate, setIsCheckingUpdate] = useState(false);
  const [debugError, setDebugError] = useState('');
  const [showDebug, setShowDebug] = useState(false);
  const roleCode = String(role || '');
  const isDesktop = !!window.desktopApp;
  const isAdmin = roleCode === 'admin';
  const isMandatoryUpdateState = ['available', 'downloading', 'installing'].includes(updateStatus?.state || '');
  const updateProgress =
    typeof updateStatus?.progress === 'number'
      ? Math.max(0, Math.min(100, updateStatus.progress))
      : 0;
  const allowedTimesheetUsers = new Set(['serik', 'suresan']);
  const canSeeTimesheet = !!username && allowedTimesheetUsers.has(username.toLowerCase());
  const navigate = useNavigate();

  useEffect(() => {
    if (!isDesktop) {
      return undefined;
    }

    const unsubscribe = window.desktopApp?.onUpdateStatus((payload) => {
      setUpdateStatus(payload);
      setUpdateDiagnostics((prev) => {
        if (!prev) return prev;
        const nextHistory = [
          ...prev.history,
          {
            timestamp: payload.timestamp || new Date().toISOString(),
            event: `renderer:${payload.state}`,
            details: payload as unknown as Record<string, unknown>,
          },
        ];
        return {
          ...prev,
          history: nextHistory.slice(-200),
        };
      });
    });

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [isDesktop]);

  useEffect(() => {
    let isCancelled = false;

    const loadDiagnostics = async () => {
      if (!isDesktop || !isAdmin) {
        return;
      }

      try {
        const payload = await window.desktopApp?.getUpdateDiagnostics();
        if (!payload) {
          return;
        }
        if (!isCancelled) {
          setUpdateDiagnostics(payload);
        }
      } catch (error) {
        if (!isCancelled) {
          setDebugError(error instanceof Error ? error.message : 'Не удалось загрузить диагностику updater.');
        }
      }
    };

    loadDiagnostics();
    const intervalId = window.setInterval(loadDiagnostics, 15000);

    return () => {
      isCancelled = true;
      window.clearInterval(intervalId);
    };
  }, [isAdmin, isDesktop]);

  const handleManualUpdateCheck = async () => {
    if (!isDesktop || !isAdmin) {
      return;
    }

    try {
      setIsCheckingUpdate(true);
      setDebugError('');
      const result = await window.desktopApp?.checkForUpdates();
      if (!result) {
        setDebugError('Desktop bridge недоступен.');
        return;
      }
      if (!result.ok) {
        setDebugError(result.error || result.message);
      }
      const latest = await window.desktopApp?.getUpdateDiagnostics();
      if (latest) {
        setUpdateDiagnostics(latest);
      }
    } catch (error) {
      setDebugError(error instanceof Error ? error.message : 'Не удалось вручную запустить проверку обновлений.');
    } finally {
      setIsCheckingUpdate(false);
    }
  };

  useEffect(() => {
    let isCancelled = false;

    const loadVersion = async () => {
      if (!isDesktop) {
        return;
      }

      try {
        const version = await window.desktopApp?.getVersion();
        if (!version) {
          return;
        }
        if (!isCancelled) {
          setAppVersion(version || '');
        }
      } catch (error) {
        console.warn('Failed to load desktop app version', error);
      }
    };

    loadVersion();

    return () => {
      isCancelled = true;
    };
  }, [isDesktop]);

  const handleLogout = async () => {
    await csrfFetch(`${BACKEND}/api/logout/`, {
      method: 'POST',
      credentials: 'include',
    });
    clearBackgroundLocalStorage();
    await refreshSession();
    navigate('/login');
  };

  const timesheetMenuItem = canSeeTimesheet ? (
    <li>
      <NavLink
        to="/timesheet"
        className={({ isActive }) =>
          isActive ? `${styles.menuItem} ${styles.active}` : styles.menuItem
        }
      >
        Табель
      </NavLink>
    </li>
  ) : null;

  const sidebarFooter = (
    <div className={styles.sidebarFooter}>
      <div>
        Nova Legal
      </div>
      {appVersion && <div className={styles.version}>{appVersion}</div>}
      {isDesktop && isAdmin && (
        <div className={styles.updateDebug}>
          <button
            type="button"
            className={styles.debugToggle}
            onClick={() => setShowDebug((prev) => !prev)}
          >
            {showDebug ? 'Скрыть debug updater' : 'Показать debug updater'}
          </button>

          {showDebug && (
            <div className={styles.debugPanel}>
              <div className={styles.debugLine}><strong>Статус:</strong> {updateStatus?.state || 'нет событий'}</div>
              <div className={styles.debugLine}><strong>Сообщение:</strong> {updateStatus?.message || '—'}</div>
              <div className={styles.debugLine}><strong>Версия app:</strong> {updateDiagnostics?.appVersion || appVersion || '—'}</div>
              <div className={styles.debugLine}><strong>Лог файл:</strong> {updateDiagnostics?.logPath || 'не найден'}</div>
              {debugError && <div className={styles.debugError}>{debugError}</div>}
              <button
                type="button"
                className={styles.debugButton}
                onClick={handleManualUpdateCheck}
                disabled={isCheckingUpdate}
              >
                {isCheckingUpdate ? 'Проверяем...' : 'Проверить обновления сейчас'}
              </button>
              <pre className={styles.debugHistory}>
                {(updateDiagnostics?.history || [])
                  .slice(-12)
                  .map((item) => `${item.timestamp} | ${item.event} | ${JSON.stringify(item.details)}`)
                  .join('\n') || 'История updater пуста.'}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const mandatoryUpdateOverlay = isMandatoryUpdateState ? (
    <div className={styles.updateOverlay}>
      <div className={styles.updateOverlayBox}>
        <div className={styles.updateOverlayTitle}>Доступно обязательное обновление</div>
        <div className={styles.updateOverlayText}>
          {updateStatus?.message || 'Найдена новая версия. Подождите завершения обновления.'}
        </div>
        <div className={styles.updateOverlayProgressLabel}>Загрузка: {updateProgress.toFixed(2)}%</div>
        <div className={styles.updateOverlayProgressBar}>
          <div
            className={styles.updateOverlayProgressBarFill}
            style={{ width: `${updateProgress}%` }}
          />
        </div>
      </div>
    </div>
  ) : null;

  if (roleCode === 'representative_employee') {
    return (
      <nav className={styles.sidebar}>
        {mandatoryUpdateOverlay}
        <div>
          <div className={styles.logoContainer}>
            <NavLink to="/chairman" className={styles.logoLink}>
              <img src={`${process.env.PUBLIC_URL}/images/logo.png`} alt="Logo" className={styles.logoImage} />
              <span className={styles.logoText}>Nova Legal</span>
            </NavLink>
          </div>
          <ul className={styles.menu}>
            <li>
              <NavLink
                to="/chairman"
                className={({ isActive }) =>
                  isActive ? `${styles.menuItem} ${styles.active}` : styles.menuItem
                }
              >
                Мои таблицы
              </NavLink>
            </li>
            <li>
              <NavLink
                to="/dossier"
                className={({ isActive }) =>
                  isActive ? `${styles.menuItem} ${styles.active}` : styles.menuItem
                }
              >
                Досье
              </NavLink>
            </li>
            <li>
              <NavLink
                to="/summary"
                className={({ isActive }) =>
                  isActive ? `${styles.menuItem} ${styles.active}` : styles.menuItem
                }
              >
                Сводка
              </NavLink>
            </li>
            <li>
              <NavLink
                to="/chsi-summary"
                className={({ isActive }) =>
                  isActive ? `${styles.menuItem} ${styles.active}` : styles.menuItem
                }
              >
                Сводка по чси
              </NavLink>
            </li>
            <li>
              <NavLink
                to="/representative/tasks"
                className={({ isActive }) =>
                  isActive ? `${styles.menuItem} ${styles.active}` : styles.menuItem
                }
              >
                Канбан
              </NavLink>
            </li>
            <li>
              <NavLink
                to="/chairman/case"
                className={({ isActive }) =>
                  isActive ? `${styles.menuItem} ${styles.active}` : styles.menuItem
                }
              >
                Дело
              </NavLink>
            </li>
            {timesheetMenuItem}
            <li onClick={handleLogout} className={styles.menuItem} style={{ cursor: 'pointer' }}>
              Выйти
            </li>
          </ul>
        </div>
        {sidebarFooter}
      </nav>
    );
  }

  if (roleCode === 'notary') {
    return (
      <nav className={styles.sidebar}>
        {mandatoryUpdateOverlay}
        <div>
          <div className={styles.logoContainer}>
            <NavLink to="/summary" className={styles.logoLink}>
              <img src={`${process.env.PUBLIC_URL}/images/logo.png`} alt="Logo" className={styles.logoImage} />
              <span className={styles.logoText}>Nova Legal</span>
            </NavLink>
          </div>
          <ul className={styles.menu}>
            <li><NavLink to="/sheets" className={({ isActive }) => isActive ? `${styles.menuItem} ${styles.active}` : styles.menuItem}>Мои таблицы</NavLink></li>
            <li><NavLink to="/dossier" className={({ isActive }) => isActive ? `${styles.menuItem} ${styles.active}` : styles.menuItem}>Досье</NavLink></li>
            <li><NavLink to="/summary" className={({ isActive }) => isActive ? `${styles.menuItem} ${styles.active}` : styles.menuItem}>Сводка</NavLink></li>
            <li><NavLink to="/chsi-summary" className={({ isActive }) => isActive ? `${styles.menuItem} ${styles.active}` : styles.menuItem}>Сводка по чси</NavLink></li>
            {timesheetMenuItem}
            <li onClick={handleLogout} className={styles.menuItem} style={{ cursor: 'pointer' }}>Выйти</li>
          </ul>
        </div>
        {sidebarFooter}
      </nav>
    );
  }


  if (roleCode === 'secondary' || roleCode === 'lawyer' || roleCode === 'mediator') {
    const legalHome = isLawyer ? '/litigation' : isMediator ? '/mediation' : '/summary';
    return (
      <nav className={styles.sidebar}>
        {mandatoryUpdateOverlay}
        <div>
          <div className={styles.logoContainer}>
            <NavLink to={legalHome} className={styles.logoLink}>
              <img src={`${process.env.PUBLIC_URL}/images/logo.png`} alt="Logo" className={styles.logoImage} />
              <span className={styles.logoText}>Nova Legal</span>
            </NavLink>
          </div>
          <ul className={styles.menu}>
            {!isLawyer && !isMediator && <li><NavLink to="/summary" className={({ isActive }) => isActive ? `${styles.menuItem} ${styles.active}` : styles.menuItem}>Сводка</NavLink></li>}
            {!isLawyer && !isMediator && <li><NavLink to="/dossier" className={({ isActive }) => isActive ? `${styles.menuItem} ${styles.active}` : styles.menuItem}>Досье</NavLink></li>}
            {isLawyer && <li><NavLink to="/litigation" className={({ isActive }) => isActive ? `${styles.menuItem} ${styles.active}` : styles.menuItem}>Просуживание</NavLink></li>}
            {isMediator && <li><NavLink to="/mediation" className={({ isActive }) => isActive ? `${styles.menuItem} ${styles.active}` : styles.menuItem}>Медиаторство</NavLink></li>}
            {isLawyer && <li><NavLink to="/litigation-status" className={({ isActive }) => isActive ? `${styles.menuItem} ${styles.active}` : styles.menuItem}>Статус просуживания</NavLink></li>}
            {isLawyer && <li><NavLink to="/litigation-summary" className={({ isActive }) => isActive ? `${styles.menuItem} ${styles.active}` : styles.menuItem}>Сводка юриста</NavLink></li>}
            {(isLawyer || isMediator) && <li><NavLink to="/templates" className={({ isActive }) => isActive ? `${styles.menuItem} ${styles.active}` : styles.menuItem}>Шаблоны</NavLink></li>}
            {timesheetMenuItem}
            <li onClick={handleLogout} className={styles.menuItem} style={{ cursor: 'pointer' }}>Выйти</li>
          </ul>
        </div>
        {sidebarFooter}
      </nav>
    );
  }

  if (roleCode === 'chairman' || roleCode === 'representative') {
    return (
      <nav className={styles.sidebar}>
        {mandatoryUpdateOverlay}
        <div>
          <div className={styles.logoContainer}>
            <NavLink to={roleCode === 'chairman' ? '/chairman' : '/summary'} className={styles.logoLink}>
              <img src={`${process.env.PUBLIC_URL}/images/logo.png`} alt="Logo" className={styles.logoImage} />
              <span className={styles.logoText}>Nova Legal</span>
            </NavLink>
          </div>
          <ul className={styles.menu}>
            <li>
              <NavLink
                to={roleCode === 'chairman' ? '/chairman' : '/summary'}
                className={({ isActive }) =>
                  isActive ? `${styles.menuItem} ${styles.active}` : styles.menuItem
                }
              >
                Мои таблицы
              </NavLink>
            </li>
            {roleCode === 'chairman' && (
              <li>
                <NavLink
                  to="/dossier"
                  className={({ isActive }) =>
                    isActive ? `${styles.menuItem} ${styles.active}` : styles.menuItem
                  }
                >
                  Досье
                </NavLink>
              </li>
            )}
            <li>
              <NavLink
                to="/summary"
                className={({ isActive }) =>
                  isActive ? `${styles.menuItem} ${styles.active}` : styles.menuItem
                }
              >
                Сводка
              </NavLink>
            </li>
            <li>
              <NavLink
                to="/chsi"
                className={({ isActive }) =>
                  isActive ? `${styles.menuItem} ${styles.active}` : styles.menuItem
                }
              >
                ЧСИ
              </NavLink>
            </li>
            <li>
              <NavLink
                to="/chsi-summary"
                className={({ isActive }) =>
                  isActive ? `${styles.menuItem} ${styles.active}` : styles.menuItem
                }
              >
                Сводка по чси
              </NavLink>
            </li>
            {roleCode === 'representative' && (
              <>
                <li>
                  <NavLink
                    to="/representative/case"
                    className={({ isActive }) =>
                      isActive ? `${styles.menuItem} ${styles.active}` : styles.menuItem
                    }
                  >
                    Дело
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to="/representative/employees"
                    className={({ isActive }) =>
                      isActive ? `${styles.menuItem} ${styles.active}` : styles.menuItem
                    }
                  >
                    Сотрудники
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to="/tasks"
                    className={({ isActive }) =>
                      isActive ? `${styles.menuItem} ${styles.active}` : styles.menuItem
                    }
                  >
                    Задачи
                  </NavLink>
                </li>
              </>
            )}
            {roleCode === 'chairman' && (
              <>
                <li>
                  <NavLink
                    to="/tasks"
                    className={({ isActive }) =>
                      isActive ? `${styles.menuItem} ${styles.active}` : styles.menuItem
                    }
                  >
                    Канбан
                  </NavLink>
                </li>


                <li>
                  <NavLink
                    to="/chairman/case"
                    className={({ isActive }) =>
                      isActive ? `${styles.menuItem} ${styles.active}` : styles.menuItem
                    }
                  >
                    Дело
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to="/chairman/employees"
                    className={({ isActive }) =>
                      isActive ? `${styles.menuItem} ${styles.active}` : styles.menuItem
                    }
                  >
                    Сотрудники
                  </NavLink>
                </li>
              </>
            )}
            {timesheetMenuItem}
            <li onClick={handleLogout} className={styles.menuItem} style={{ cursor: 'pointer' }}>
              Выйти
            </li>
          </ul>
        </div>
        {sidebarFooter}
      </nav>
    );
  }

  return (
    <nav className={styles.sidebar}>
      {mandatoryUpdateOverlay}
      <div>
        <div className={styles.logoContainer}>
          <NavLink to="/sheets" className={styles.logoLink}>
            <img src={`${process.env.PUBLIC_URL}/images/logo.png`} alt="Logo" className={styles.logoImage} />
            <span className={styles.logoText}>Nova Legal</span>
          </NavLink>
        </div>
        <ul className={styles.menu}>
          <li>
            <NavLink
              to="/sheets"
              className={({ isActive }) =>
                isActive ? `${styles.menuItem} ${styles.active}` : styles.menuItem
              }
            >
              Таблицы
            </NavLink>
          </li>

          <li>
            <NavLink
              to="/summary"
              className={({ isActive }) =>
                isActive ? `${styles.menuItem} ${styles.active}` : styles.menuItem
              }
            >
              Сводка
            </NavLink>
          </li>
          <li>
            <NavLink
              to="/chsi"
              className={({ isActive }) =>
                isActive ? `${styles.menuItem} ${styles.active}` : styles.menuItem
              }
            >
              ЧСИ
            </NavLink>
          </li>
          <li>
            <NavLink
              to="/chsi-summary"
              className={({ isActive }) =>
                isActive ? `${styles.menuItem} ${styles.active}` : styles.menuItem
              }
            >
              Сводка по чси
            </NavLink>
          </li>

          <li>
            <NavLink
              to="/tasks"
              className={({ isActive }) =>
                isActive ? `${styles.menuItem} ${styles.active}` : styles.menuItem
              }
            >
              Задачи
            </NavLink>
          </li>

          <li>
            <NavLink
              to="/documents"
              className={({ isActive }) =>
                isActive ? `${styles.menuItem} ${styles.active}` : styles.menuItem
              }
            >
              Документы
            </NavLink>
          </li>
          <li>
            <NavLink
              to="/dossier"
              className={({ isActive }) =>
                isActive ? `${styles.menuItem} ${styles.active}` : styles.menuItem
              }
            >
              Досье
            </NavLink>
          </li>

          <li>
            <NavLink
              to="/statements-list"
              className={({ isActive }) =>
                isActive ? `${styles.menuItem} ${styles.active}` : styles.menuItem
              }
            >
              Автопроцессы
            </NavLink>
          </li>


          <li>
            <NavLink
              to="/agents-data"
              className={({ isActive }) =>
                isActive ? `${styles.menuItem} ${styles.active}` : styles.menuItem
              }
            >
              Данные агентов
            </NavLink>
          </li>

          {(role === 'admin' || role === 'manager') && (
            <>
              <li>
                <NavLink
                  to="/history"
                  className={({ isActive }) =>
                    isActive ? `${styles.menuItem} ${styles.active}` : styles.menuItem
                  }
                >
                  История
                </NavLink>
              </li>
              <li>
                <NavLink
                  to="/history-changes"
                  className={({ isActive }) =>
                    isActive ? `${styles.menuItem} ${styles.active}` : styles.menuItem
                  }
                >
                  История изменений
                </NavLink>
              </li>
            </>
          )}

          {timesheetMenuItem}

          <li onClick={handleLogout} className={styles.menuItem} style={{ cursor: 'pointer' }}>
            Выйти
          </li>
        </ul>
      </div>
      {sidebarFooter}
    </nav>
  );
};

export default Sidebar;
